'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Facebook, Twitter, Instagram, Linkedin, Plus, Minus } from 'lucide-react';
import { useAdvisorButton } from '@/contexts/AdvisorButtonContext';
import { getExternalLinkRel } from '@/lib/utils';
import { isNativePlatform } from '@/lib/native';
import { StoreBadges } from '@/components/app-download/StoreBadges';

interface CityInfo {
  name: string;
  slug: string;
}

export interface FooterData {
  citySlug: string;
  cityName: string;
  groupedServices: Record<string, Array<{ name: string; path: string }>>;
  staticLinks: Array<{ name: string; path: string }>;
  cities?: CityInfo[];
}

interface FooterProps {
  initialCitySlug?: string;
  footerData?: FooterData | null;
  allFooterData?: Record<string, FooterData | null>;
  cities?: CityInfo[];
}

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift();
    return cookieValue ? decodeURIComponent(cookieValue) : null;
  }
  return null;
};

export function Footer({ initialCitySlug = 'bangalore', footerData = null, allFooterData = {}, cities = [] }: FooterProps) {
  const { openModal } = useAdvisorButton();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [activeCity, setActiveCity] = useState(initialCitySlug);
  const [isNative, setIsNative] = useState(false);
  const currentYear = new Date().getFullYear();

  // Hide SEO footer when running inside the Capacitor native app shell
  useEffect(() => {
    setIsNative(isNativePlatform());
  }, []);

  // Sync active city with cookie on mount
  useEffect(() => {
    const cookieCity = getCookie('selectedCity');
    if (cookieCity && (allFooterData[cookieCity] || cookieCity === initialCitySlug)) {
      setActiveCity(cookieCity);
    }
  }, [allFooterData, initialCitySlug]);

  // Listen for city changes from the header
  useEffect(() => {
    const handleCityChanged = () => {
      const cookieCity = getCookie('selectedCity');
      if (cookieCity) {
        setActiveCity(cookieCity);
      }
    };
    window.addEventListener('cityChanged', handleCityChanged);
    return () => window.removeEventListener('cityChanged', handleCityChanged);
  }, []);

  // Don't render SEO footer inside the native (Capacitor) app shell
  if (isNative) {
    return null;
  }

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  // Use allFooterData if available, otherwise fall back to single footerData prop
  const currentFooterData = allFooterData[activeCity] || footerData;
  if (!currentFooterData) return null;

  // Get city list for toggle — derive from allFooterData keys or cities prop
  const availableCities = cities.length > 0
    ? cities.filter((c) => allFooterData[c.slug])
    : Object.keys(allFooterData).map((slug) => ({
        name: allFooterData[slug]?.cityName || slug.charAt(0).toUpperCase() + slug.slice(1),
        slug,
      }));

  const documents = Object.entries(currentFooterData.groupedServices).sort(([a], [b]) =>
    a.localeCompare(b)
  );

  return (
    <footer className="bg-gray-900 text-gray-300 border-t border-gray-800">
      <div className="container py-10">
        {/* Header with Logo and Social Links */}
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-8 pb-8 border-b border-gray-800">
          <div className="flex flex-col items-center md:items-start space-y-4">
            <Link href="/" className="inline-block">
              <Image
                src="/docupro.png"
                alt="DocuPro Logo"
                width={120}
                height={35}
                className="w-28 h-auto opacity-90 hover:opacity-100 transition-opacity"
                style={{ height: 'auto' }}
              />
            </Link>

            <div className="flex space-x-3">
              <a
                href="https://www.facebook.com/DocuProOfficial"
                target="_blank"
                rel={getExternalLinkRel('https://www.facebook.com/DocuProOfficial', 'noopener noreferrer')}
                className="p-2 bg-gray-800 rounded-full hover:bg-blue-600 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href="https://www.instagram.com/docuproofficial"
                target="_blank"
                rel={getExternalLinkRel('https://www.instagram.com/docuproofficial', 'noopener noreferrer')}
                className="p-2 bg-gray-800 rounded-full hover:bg-pink-600 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href="https://x.com/OfficialDocuPro"
                target="_blank"
                rel={getExternalLinkRel('https://x.com/OfficialDocuPro', 'noopener noreferrer')}
                className="p-2 bg-gray-800 rounded-full hover:bg-gray-700 transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="h-4 w-4" />
              </a>
              <a
                href="https://www.linkedin.com/company/docuproofficial"
                target="_blank"
                rel={getExternalLinkRel('https://www.linkedin.com/company/docuproofficial', 'noopener noreferrer')}
                className="p-2 bg-gray-800 rounded-full hover:bg-blue-700 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div className="flex-1 text-center md:text-left">
            <p className="text-sm text-gray-400 leading-relaxed max-w-3xl">
              DocuPro simplifies government paperwork. Aadhaar, PAN, Passport, Driving License – we handle it all.
              Fast, reliable, trusted by thousands across India.
            </p>
          </div>

          {/* Store badges. This footer is hidden inside the native shell (see
              the isNative bail-out above), so these only ever show on the web
              — no "download the app" prompt to someone already in the app. */}
          <div className="flex flex-col items-center gap-3 md:items-start">
            <h3 className="text-sm font-semibold text-white">Get the app</h3>
            <StoreBadges badgeHeightClass="h-11" className="justify-center md:justify-start" />
          </div>
        </div>

        {/* City-Specific Services Grid */}
        {documents.length > 0 && (
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
              <h3 className="text-lg font-semibold text-white">
                Services in {currentFooterData.cityName}
              </h3>
              {availableCities.length > 1 && (
                // flex-wrap, not a single nowrap row — with enough cities this row is wider than
                // any mobile viewport, and since nothing here was clipping/scrolling it, it was
                // dragging the ENTIRE page's scrollWidth out past the viewport (visible as the
                // whole site needing a horizontal scroll, not just this row).
                <div className="flex flex-wrap gap-2">
                  {availableCities.map((city) => (
                    <button
                      key={city.slug}
                      onClick={() => setActiveCity(city.slug)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                        activeCity === city.slug
                          ? 'bg-blue-600/20 border-blue-600 text-blue-400'
                          : 'bg-gray-800/50 border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'
                      }`}
                    >
                      {city.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col md:block md:columns-3 lg:columns-4 md:gap-x-4">
              {documents.map(([documentName, services]) => (
                <div key={documentName} className="border-b border-gray-800 md:border md:border-gray-700 md:rounded-md md:break-inside-avoid md:mb-4">
                  <button
                    onClick={() => toggleSection(documentName)}
                    className="flex items-center justify-between w-full text-left py-3 md:py-2.5 md:px-3 md:cursor-default md:border-b md:border-gray-700"
                  >
                    <h4 className="text-sm font-semibold text-white">{documentName}</h4>
                    <span className="md:hidden text-gray-400">
                      {expandedSections[documentName] ? (
                        <Minus className="h-4 w-4" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                    </span>
                  </button>
                  <ul
                    className={`space-y-1.5 text-sm py-2.5 md:px-3 ${
                      expandedSections[documentName] ? 'block' : 'hidden md:block'
                    }`}
                  >
                    {services.map((service, idx) => (
                      <li key={idx}>
                        <Link
                          href={service.path}
                          className="hover:text-white transition-colors line-clamp-1"
                        >
                          {service.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bottom Bar with centered links */}
        <div className="pt-8 border-t border-gray-800">
          <div className="flex flex-col items-center gap-4 text-center">
            <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2">
              {currentFooterData.staticLinks.map((link, idx) => (
                <Link
                  key={idx}
                  href={link.path}
                  className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
                >
                  {link.name}
                </Link>
              ))}
              {/* <button
                type="button"
                onClick={openModal}
                className="text-xs text-blue-400 hover:text-blue-300 transition-colors font-medium"
              >
                Connect with Advisor
              </button> */}
            </nav>

            <div className="text-[10px] text-gray-600" suppressHydrationWarning>
              &copy; {currentYear} DocuPro Services Pvt Ltd
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
